/**
 * How fast capture actually is, measured rather than assumed.
 *
 * Stage 1's exit gate is a number -- a photo-only item saved in under about
 * fifteen seconds on a phone, and fifty items in a sitting without wanting to
 * quit -- and a gate nobody can measure is a gate that gets waved through. So
 * the app times itself: the clock starts when the capture screen opens (which
 * is the tap the gate is about) and stops when the item and its photos are
 * saved.
 *
 * **Local, and deliberately so.** The roadmap says simple logging is fine, and
 * this is one person's own numbers on their own device, so it lives in
 * localStorage rather than in a table somebody would have to build, migrate and
 * eventually explain. There is nothing here worth sending anywhere, and the
 * moment there is, it becomes a considered feature rather than a leftover.
 *
 * Everything that touches storage is wrapped: private windows, cleared site
 * data and blocked storage all throw rather than answering empty, and a capture
 * screen that crashes because it could not write a stopwatch would be a very
 * silly way to fail.
 */

const KEY = "iltocat.capture.v1";
const SESSION_KEY = "iltocat.capture.session";

/** The last two hundred saves. Enough for a sitting and its predecessor, and small enough to parse. */
const KEEP = 200;

export interface CaptureRecord {
  /** Tap to saved, in milliseconds. */
  ms: number;
  photos: number;
  /** When it was saved, epoch milliseconds. */
  at: number;
}

export interface CaptureStats {
  /** Saves recorded on this device, ever (up to what is kept). */
  total: number;
  /** Saves since this tab was opened, which is what "a sitting" means in practice. */
  thisSession: number;
  /** The middle time, not the mean: one interrupted capture should not move the number. */
  medianMs: number | null;
  fastestMs: number | null;
}

let startedAt: number | null = null;

/** Called when the capture screen opens. That tap is what the gate's clock is about. */
export const startCapture = (): void => {
  startedAt = Date.now();
};

/**
 * Called when the item and all of its photos are saved. A capture with no start
 * -- a reload halfway through, say -- records nothing rather than a made-up
 * duration, because a wrong number here is worse than no number.
 */
export function finishCapture(photos: number): CaptureRecord | null {
  if (startedAt === null) return null;

  const record: CaptureRecord = { ms: Date.now() - startedAt, photos, at: Date.now() };
  startedAt = null;

  const kept = [...read(), record].slice(-KEEP);
  write(kept);
  return record;
}

/** Called when the capture screen closes without saving, so an abandoned start does not leak into the next. */
export const cancelCapture = (): void => {
  startedAt = null;
};

export function captureStats(): CaptureStats {
  const records = read();
  if (records.length === 0) return { total: 0, thisSession: 0, medianMs: null, fastestMs: null };

  const times = records.map((record) => record.ms).sort((a, b) => a - b);
  const middle = Math.floor(times.length / 2);

  return {
    total: records.length,
    thisSession: records.filter((record) => record.at >= sessionStart()).length,
    medianMs:
      times.length % 2 === 1
        ? (times[middle] ?? null)
        : Math.round(((times[middle - 1] ?? 0) + (times[middle] ?? 0)) / 2),
    fastestMs: times[0] ?? null,
  };
}

/** "11.4 s", or "1 m 05 s" for the ones worth noticing. */
export function readableTime(ms: number): string {
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes} m ${String(seconds).padStart(2, "0")} s`;
}

/* ------------------------------------------------------------- plumbing -- */

function read(): CaptureRecord[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (record): record is CaptureRecord =>
        typeof record === "object" && record !== null && typeof (record as CaptureRecord).ms === "number",
    );
  } catch {
    return [];
  }
}

function write(records: CaptureRecord[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(records));
  } catch {
    // A full or blocked storage costs a statistic, not a capture.
  }
}

/** When this tab was first opened, which is as good a definition of "a sitting" as this app needs. */
function sessionStart(): number {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return Number(existing);

    const now = Date.now();
    window.sessionStorage.setItem(SESSION_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}
